import React, { useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast"; // Corrected import path to hooks directory
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem, // Assuming SelectItem is needed within SelectContent
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge"; // Assuming Badge might be used for currency/status
import {
  Music,
  PlusCircle,
  RefreshCw,
  Upload,
  Volume2,
  Grid,
  FileType,
  Check,
  Trash2,
  Play, // Added Play icon for audio player
  Pause, // Added Pause icon for audio player
  Coins, // Added Coins icon for currency
  Gem, // Added Gem icon for premium currency
} from "lucide-react";

// Define sound types
type SoundType = 'gameplay' | 'ui' | 'win' | 'lose';

// Sound categories mapping
const soundCategories = [
  { id: 'gameplay' as SoundType, name: 'Gameplay Sounds', description: 'Sounds that play during gameplay' },
  { id: 'ui' as SoundType, name: 'UI Sounds', description: 'Menu and interface sounds' },
  { id: 'win' as SoundType, name: 'Victory Sounds', description: 'Sounds that play when you win' },
  { id: 'lose' as SoundType, name: 'Defeat Sounds', description: 'Sounds that play when you lose' },
];

// Define the shape of a sound item (adjust based on actual API response)
interface SoundItem {
  id: number;
  name: string;
  description: string;
  audioUrl: string;
  soundType: SoundType;
  price: number;
  currencyType: 'regular' | 'premium';
  isActive: boolean;
  // Add other relevant fields if necessary
}


export function SoundManagementTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient(); // Get queryClient instance
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid'); // Add view mode state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    soundType: "gameplay" as SoundType,
    isActive: true,
    price: 100,
    currencyType: "regular" as "regular" | "premium",
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [soundToDelete, setSoundToDelete] = useState<SoundItem | null>(null);
  const [playingAudio, setPlayingAudio] = useState<HTMLAudioElement | null>(null); // State for currently playing audio


  // Handle file selection change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Handle audio upload
  const handleAudioUpload = () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select an audio file to upload.",
        variant: "destructive",
      });
      return;
    }

    // Ensure it's an audio file
    if (!selectedFile.type.startsWith('audio/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an audio file (MP3, WAV, etc.).",
        variant: "destructive",
      });
      return;
    }

    // Create FormData for upload
    const formDataObj = new FormData();
    formDataObj.append('file', selectedFile);
    formDataObj.append('soundType', formData.soundType);

    // Upload the audio file
    uploadAudioMutation.mutate(formDataObj);
  };

  // Mutation to upload audio file
  const uploadAudioMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return apiRequest<{ audioUrl: string, soundType: string }>(
        "/api/currency/shop/audio-upload",
        {
          method: "POST",
          body: formData,
        },
      );
    },
    onSuccess: (data) => {
      setSelectedFile(null);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Combine the audio data with the rest of the form to create a shop item
      const shopItemData = {
        name: formData.name,
        description: formData.description,
        audioUrl: data.audioUrl,
        soundType: data.soundType,
        itemType: "sound", // Mark as a sound item
        category: data.soundType, // Category matches sound type
        price: formData.price,
        currencyType: formData.currencyType,
        isActive: formData.isActive,
        stock: null, // Sounds don't have limited stock
      };

      // Add it to the shop
      addShopItemMutation.mutate(shopItemData);
    },
    onError: (error: Error) => {
      toast({
        title: "Error uploading audio",
        description:
          error.message || "Failed to upload the audio file. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to add a shop item with the uploaded audio
  const addShopItemMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest<any>("/api/currency/shop", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      // Invalidate both shop queries to update the UI
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop"] });
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop/sounds"] });
      toast({
        title: "Sound added successfully",
        description: "The new sound has been added to the shop.",
      });
      setIsAddDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding sound",
        description:
          error.message || "Failed to add the sound to the shop. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to delete a sound
  const deleteSoundMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest<any>(`/api/currency/shop/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      // Invalidate both shop queries to update the UI
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop"] });
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop/sounds"] });
      toast({
        title: "Sound deleted successfully",
        description: "The sound has been removed from the shop.",
      });
      setIsDeleteDialogOpen(false);
      setSoundToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting sound",
        description:
          error.message || "Failed to delete the sound. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to toggle the active status of a sound
  const toggleActiveMutation = useMutation({
    mutationFn: async ({id, isActive}: {id: number, isActive: boolean}) => {
      return apiRequest<any>(`/api/currency/shop/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isActive }),
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate both shop queries to update the UI
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop"] });
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop/sounds"] });
      toast({
        title: variables.isActive ? "Sound activated" : "Sound deactivated",
        description: variables.isActive
          ? "The sound is now available in the shop."
          : "The sound has been deactivated and is no longer available.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating sound",
        description:
          error.message || "Failed to update the sound status. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Query to get all sound shop items
  const {
    data: soundItems,
    isLoading,
    error,
    refetch,
  } = useQuery<SoundItem[]>({ // Use the SoundItem interface
    queryKey: ["/api/currency/shop/sounds"],
    queryFn: () => apiRequest<any[]>("/api/currency/shop")
      .then(items => items.filter(item => item.itemType === "sound")),
  });

  // Reset form values
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      soundType: "gameplay",
      isActive: true,
      price: 100,
      currencyType: "regular",
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Function to play/pause audio
  const togglePlayAudio = (audioUrl: string) => {
    if (playingAudio && playingAudio.src === audioUrl && !playingAudio.paused) {
      playingAudio.pause();
      setPlayingAudio(null); // Clear playing state when paused
    } else {
      // Pause any currently playing audio
      if (playingAudio) {
        playingAudio.pause();
      }
      // Play the new audio
      const audio = new Audio(audioUrl);
      audio.play();
      audio.onended = () => setPlayingAudio(null); // Clear state when finished
      audio.onpause = () => setPlayingAudio(null); // Clear state if paused externally
      setPlayingAudio(audio);
    }
  };

  // Function to render currency icon
  const renderCurrencyIcon = (type: 'regular' | 'premium') => {
    return type === 'premium' ? (
      <Gem className="h-4 w-4 text-purple-500" />
    ) : (
      <Coins className="h-4 w-4 text-yellow-600" />
    );
  };


  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading sounds</h3>
        <p className="text-muted-foreground">{(error as Error).message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Group sounds by category
  const soundsByCategory = soundCategories.map(category => ({
    ...category,
    sounds: soundItems?.filter(item => item.soundType === category.id) || []
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Music className="mr-2 h-6 w-6" />
            Sound Management
          </h2>
          <p className="text-muted-foreground">
            Upload and manage sound effects for the game
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                resetForm();
                setIsAddDialogOpen(true);
              }}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add New Sound
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Sound</DialogTitle>
              <DialogDescription>
                Upload a sound file and add it to the shop
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="soundName">Sound Name</Label>
                <Input
                  id="soundName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter a name for this sound"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the sound"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="soundType">Sound Category</Label>
                <Select
                  value={formData.soundType}
                  onValueChange={(value: string) => setFormData({ ...formData, soundType: value as SoundType })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sound category" />
                  </SelectTrigger>
                  <SelectContent>
                    {soundCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                    placeholder="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currencyType">Currency Type</Label>
                  <Select
                    value={formData.currencyType}
                    onValueChange={(value: string) => setFormData({ ...formData, currencyType: value as "regular" | "premium" })}
                  >
                     <SelectTrigger>
                       <SelectValue placeholder="Select currency" />
                     </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular (Coins)</SelectItem>
                      <SelectItem value="premium">Premium (Gems)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="isActive">Active in Shop</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="soundFile">Sound File (MP3, WAV)</Label>
                <Input
                  id="soundFile"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="audio/*"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Audio files will be stored in Digital Ocean at /spar/sounds/{formData.soundType}/
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAudioUpload}
                disabled={!selectedFile || !formData.name || uploadAudioMutation.isPending || addShopItemMutation.isPending}
              >
                {(uploadAudioMutation.isPending || addShopItemMutation.isPending) ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload & Add Sound
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sound</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the sound "{soundToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSoundToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (soundToDelete) {
                  deleteSoundMutation.mutate(soundToDelete.id);
                }
              }}
              disabled={deleteSoundMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSoundMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Sound"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Tabs defaultValue="gameplay" className="w-full">
        <TabsList className="flex w-full overflow-x-auto space-x-2 pb-2">
          {soundCategories.map(category => (
            <TabsTrigger key={category.id} value={category.id} className="flex-shrink-0">
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {soundCategories.map(category => (
          <TabsContent key={category.id} value={category.id} className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">{category.name}</h3>
                    <p className="text-sm text-muted-foreground">{category.description}</p>
                </div>
                 {/* View toggle buttons */}
                 <div className="bg-background border rounded-md p-1 inline-flex">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`px-3 py-1.5 rounded-sm ${viewMode === 'grid' ? 'bg-muted' : ''}`}
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid className="h-4 w-4" />
                      <span className="sr-only">Grid View</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`px-3 py-1.5 rounded-sm ${viewMode === 'list' ? 'bg-muted' : ''}`}
                      onClick={() => setViewMode('list')}
                    >
                      <FileType className="h-4 w-4" />
                      <span className="sr-only">List View</span>
                    </Button>
                  </div>
            </div>


            {!soundsByCategory.find(c => c.id === category.id)?.sounds.length ? (
              <div className="p-8 text-center border border-dashed rounded-lg">
                <Volume2 className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No sounds added to this category yet</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    resetForm();
                    setFormData(prev => ({ ...prev, soundType: category.id as SoundType }));
                    setIsAddDialogOpen(true);
                  }}
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add {category.name}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">


                {viewMode === 'grid' ? (
                  // Grid view
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {soundsByCategory.find(c => c.id === category.id)?.sounds.map(sound => (
                      <Card key={sound.id} className="overflow-hidden flex flex-col">
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between text-base">
                            <span>{sound.name}</span>
                             <Badge variant={sound.isActive ? "default" : "outline"} className="ml-2">
                                {sound.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </CardTitle>
                          <CardDescription className="text-xs">{sound.description || "No description"}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex-grow">
                          {sound.audioUrl && (
                             <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => togglePlayAudio(sound.audioUrl)}
                                    className="h-8 w-8"
                                >
                                    {playingAudio && playingAudio.src === sound.audioUrl && !playingAudio.paused ? (
                                        <Pause className="h-4 w-4" />
                                    ) : (
                                        <Play className="h-4 w-4" />
                                    )}
                                </Button>
                                <span className="text-sm text-muted-foreground truncate">{sound.audioUrl.split('/').pop()}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t">
                             <div className="flex items-center space-x-1">
                                {renderCurrencyIcon(sound.currencyType)}
                                <span>{sound.price}</span>
                             </div>
                             <span className="text-xs text-muted-foreground">ID: {sound.id}</span>
                          </div>
                        </CardContent>
                        <CardFooter className="gap-2 flex border-t pt-4">
                          <Button
                            variant="destructive"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                                setSoundToDelete(sound);
                                setIsDeleteDialogOpen(true);
                            }}
                            disabled={deleteSoundMutation.isPending && soundToDelete?.id === sound.id}
                          >
                             {deleteSoundMutation.isPending && soundToDelete?.id === sound.id ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                              )}
                            Delete
                          </Button>
                          <Button
                            variant={sound.isActive ? "outline" : "default"}
                            size="sm"
                            className="flex-1"
                            onClick={() => toggleActiveMutation.mutate({ id: sound.id, isActive: !sound.isActive })}
                            disabled={toggleActiveMutation.isPending}
                          >
                             {toggleActiveMutation.isPending ? (
                                 <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                             ) : sound.isActive ? (
                                <>
                                    <Check className="h-4 w-4 mr-2 text-red-500" /> {/* Using Check but maybe X icon? */}
                                    Deactivate
                                </>
                             ) : (
                                <>
                                    <Check className="h-4 w-4 mr-2 text-green-500" />
                                    Activate
                                </>
                             )}
                          </Button>
                        </CardFooter>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // List view
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Audio</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {soundsByCategory.find(c => c.id === category.id)?.sounds.map(sound => (
                          <TableRow key={sound.id}>
                             <TableCell>
                                <div className="font-medium">{sound.name}</div>
                                <div className="text-xs text-muted-foreground">{sound.description || "No description"}</div>
                             </TableCell>
                             <TableCell>
                                {sound.audioUrl && (
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => togglePlayAudio(sound.audioUrl)}
                                        className="h-8 w-8"
                                    >
                                        {playingAudio && playingAudio.src === sound.audioUrl && !playingAudio.paused ? (
                                            <Pause className="h-4 w-4" />
                                        ) : (
                                            <Play className="h-4 w-4" />
                                        )}
                                    </Button>
                                )}
                             </TableCell>
                             <TableCell>
                                <div className="flex items-center space-x-1">
                                    {renderCurrencyIcon(sound.currencyType)}
                                    <span>{sound.price}</span>
                                </div>
                             </TableCell>
                             <TableCell>
                                <Badge variant={sound.isActive ? "default" : "outline"}>
                                    {sound.isActive ? "Active" : "Inactive"}
                                </Badge>
                             </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant={sound.isActive ? "outline" : "default"}
                                  size="sm"
                                  onClick={() => toggleActiveMutation.mutate({ id: sound.id, isActive: !sound.isActive })}
                                  disabled={toggleActiveMutation.isPending}
                                >
                                  {toggleActiveMutation.isPending ? (
                                     <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                  ) : sound.isActive ? (
                                    <>
                                      {/* <X className="h-4 w-4 mr-1" /> */} Deactivate {/* Replace Check with X or similar */}
                                    </>
                                  ) : (
                                    <>
                                      <Check className="h-4 w-4 mr-1" />
                                      Activate
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => {
                                    setSoundToDelete(sound);
                                    setIsDeleteDialogOpen(true);
                                  }}
                                  disabled={deleteSoundMutation.isPending && soundToDelete?.id === sound.id}
                                >
                                  {deleteSoundMutation.isPending && soundToDelete?.id === sound.id ? (
                                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4 mr-1" />
                                  )}
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
